import React from 'react';
import { withRouter } from 'react-router-dom';

// local dependencies
import { memberDetailsAlertsStatsWidget as MemberDetailsAlertsStatsWidgetView } from 'ui/views/widgets/memberDetailsAlertsStatsWidget';
import { MemberDetailsAlertsStatsWidgetService } from 'services/memberDetailsAlertsStatsWidget';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { getWidgetData } from 'resolvers/getWidgetData';
import { WIDGET_NAMES } from 'constants/entity-names';
import { DynamicQueueTask } from 'tasks/DynamicQueue';
import { noop } from 'lodash';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsAlertsStatsWidget } from 'ui/containers/MemberDetailsAlertsStatsWidget';
 *
 * // render
 * <MemberDetailsAlertsStatsWidget propName='propValue'/>
 */
class _MemberDetailsAlertsStatsWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // initial state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            patientId: props.requestPayload.context.patientId,
            componentData: {}
        };

        // create new service instance
        this.memberDetailsAlertsStatsWidgetService = new MemberDetailsAlertsStatsWidgetService();

        // bind methods and listeners
        this.__bind();

        this.queue = new DynamicQueueTask( ( next ) => {
            
            // start component data fetch
            this.fetchComponentData( next );
        } );

    }

    /**
     * @desc Handles binding of methods and listeners
     */
    __bind = () => {
        log.debug( 'MemberDetailsAlertsStatsWidget.__bind()' );

        MessageBus.on( `${ WIDGET_NAMES.MEMBER_DETAILS_ALERTS_STATS }_${ MESSAGE_BUS_EVENTS.WIDGET_RELOAD_REQUEST }`, this.handleWebSocketMessages, this );
        MessageBus.on( `${ WIDGET_NAMES.MEMBER_DETAILS_ALERTS_STATS }_${ MESSAGE_BUS_EVENTS.WIDGET_SYNC_REQUEST }`, this.handleWebSocketMessages, this );
    }

    /**
     * @desc Fetch component data
     */
    fetchComponentData = async ( runNext = noop ) => {
        log.debug( 'MemberDetailsAlertsStatsWidget.fetchComponentData()' );

        try {
            const { status, data }  = await this.memberDetailsAlertsStatsWidgetService.fetchComponentData( {
                url: this.props.widget.url,
                data: this.props.requestPayload
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                }, () =>  runNext() );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data
                }, () =>  runNext() );
            }
        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            }, () =>  runNext() );
        }
    }

    /**
     * @desc Handles clicking on alerts stats card
     */
    handleCardClick = () => {
        log.debug( 'MemberDetailsAlertsStatsWidget.handleCardClick()' );

        this.props.history.push( {
            pathname: DASHBOARD_SUB_ROUTES.MEMBER_ALERTS,
            state: {  memberId: this.props.memberId, patientId: this.state.patientId }
        } );
    }

    /**
     * Handles WebSocket messages
     * @param {object} message - Object containing data received via WebSocket
     */
    handleWebSocketMessages = ( message ) => {
        this.queue.push( async ( next ) => {
            this.setState( await getWidgetData( {
                message,
                url: this.props.widget.url,
                initialState: this.state,
                serviceInstance: new MemberDetailsAlertsStatsWidgetService()
            } ), () => {
                MessageBus.trigger( `${ MESSAGE_BUS_EVENTS.UPDATE_FLYOUT }`, { id: message.id } );
                next();
            } );
        } );
    }

    /**
     * @desc Handles unbinding of methods and listeners
     */
    __unbind = () => {
        log.debug( 'MemberDetailsAlertsStatsWidget.__unbind()' );

        MessageBus.off( `${ WIDGET_NAMES.MEMBER_DETAILS_ALERTS_STATS }_${ MESSAGE_BUS_EVENTS.WIDGET_RELOAD_REQUEST }`, this.handleWebSocketMessages, this );
        MessageBus.off( `${ WIDGET_NAMES.MEMBER_DETAILS_ALERTS_STATS }_${ MESSAGE_BUS_EVENTS.WIDGET_SYNC_REQUEST }`, this.handleWebSocketMessages, this );
    }

    /**
     * @desc When component unmounts, cleanup resources.
     */
    componentWillUnmount() {
        this.__unbind();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailsAlertsStatsWidgetView
                { ...this.state }
                onCardClick={ this.handleCardClick }
            />
        );
    }
}

// set display name
_MemberDetailsAlertsStatsWidget.displayName = 'MemberDetailsAlertsStatsWidget';

// set default props
_MemberDetailsAlertsStatsWidget.defaultProps = {};

// prop types
_MemberDetailsAlertsStatsWidget.propTypes = {};


export const MemberDetailsAlertsStatsWidget = withRouter( _MemberDetailsAlertsStatsWidget );
